import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  Input,
  FormControl,
  FormLabel,
  useToast,
  useColorModeValue,
  Divider,
  Badge,
  IconButton
} from '@chakra-ui/react';
import { FiPlus, FiX, FiEdit } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { listOwnerSeries, type SeriesDoc, getSeasonsForSeries } from '../services/contentService';
import { createContentDocumentDirect } from '../services/uploadService';

interface Series {
  id: string;
  title: string;
  description?: string;
  seasons: Season[];
}

interface Season {
  id: string;
  title: string;
  description?: string;
  episodeCount: number;
}

interface SeriesSeasonPickerProps {
  selectedSeriesId?: string | null;
  selectedSeasonId?: string | null;
  onSeriesChange: (seriesId: string | null) => void;
  onSeasonChange: (seasonId: string | null) => void;
  onNewSeries: (seriesData: { title: string; description: string }) => void;
  onNewSeason: (seasonData: { title: string; description: string }) => void;
  disabled?: boolean;
}

const SeriesSeasonPicker: React.FC<SeriesSeasonPickerProps> = ({
  selectedSeriesId,
  selectedSeasonId,
  onSeriesChange,
  onSeasonChange,
  onNewSeries,
  onNewSeason,
  disabled = false
}) => {
  const toast = useToast();
  const { user } = useAuth();
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewSeries, setShowNewSeries] = useState(false);
  const [showNewSeason, setShowNewSeason] = useState(false);
  const [newSeriesTitle, setNewSeriesTitle] = useState('');
  const [newSeriesDescription, setNewSeriesDescription] = useState('');
  const [newSeasonTitle, setNewSeasonTitle] = useState('');
  const [newSeasonDescription, setNewSeasonDescription] = useState('');

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (user?.uid) {
      loadSeries();
    } else {
      setLoading(false);
    }
  }, [user?.uid]);

  const loadSeries = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Load only the user's own series
      const userSeries = await listOwnerSeries(user.uid);
      
      // Transform SeriesDoc to Series format and load seasons for each series
      const transformedSeriesPromises = userSeries.map(async (s: SeriesDoc) => {
        const seriesId = s.id || '';
        let seasons: Season[] = [];
        
        // Load seasons for this series
        try {
          const seasonsData = await getSeasonsForSeries(seriesId);
          seasons = seasonsData.map(seasonData => ({
            id: seasonData.seasonNumber,
            title: `Season ${seasonData.seasonNumber}`,
            description: `Season ${seasonData.seasonNumber} with ${seasonData.episodeCount} episodes`,
            episodeCount: seasonData.episodeCount
          }));
        } catch (error) {
          console.error(`Error loading seasons for series ${seriesId}:`, error);
          // Continue with empty seasons array
        }
        
        return {
          id: seriesId,
          title: s.title || 'Untitled Series',
          description: s.description || '',
          seasons
        };
      });
      
      const transformedSeries = await Promise.all(transformedSeriesPromises);
      setSeries(transformedSeries);
    } catch (error) {
      console.error('Error loading series:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your series. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedSeries = series.find(s => s.id === selectedSeriesId);
  const availableSeasons = selectedSeries?.seasons || [];

  const handleCreateSeries = async () => {
    if (!newSeriesTitle.trim()) {
      toast({
        title: 'Error',
        description: 'Series title is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!user?.uid) {
      toast({
        title: 'Error',
        description: 'Please sign in to create a series',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Create series in Firestore
      const seriesPayload = {
        title: newSeriesTitle.trim(),
        description: newSeriesDescription.trim() || '',
        category: 'series' as const,
        visibility: 'public' as const,
        rating: '',
        language: 'English',
        genres: [] as string[],
        tags: '',
        linkedContent: '',
        seasonNumber: '',
        episodeNumber: '',
        chapterNumber: '',
        cast: '',
        director: '',
        writer: '',
        contentAdvisory: '',
      };

      const seriesId = await createContentDocumentDirect(user.uid, seriesPayload, {});
      
      // Call the parent callback
      onNewSeries({
        title: newSeriesTitle.trim(),
        description: newSeriesDescription.trim()
      });

      // Reload series list to show the new series
      await loadSeries();
      
      // Select the newly created series
      onSeriesChange(seriesId);

      toast({
        title: 'Series Created',
        description: `"${newSeriesTitle.trim()}" has been created successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setNewSeriesTitle('');
      setNewSeriesDescription('');
      setShowNewSeries(false);
    } catch (error: any) {
      console.error('Error creating series:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create series. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateSeason = async () => {
    if (!newSeasonTitle.trim()) {
      toast({
        title: 'Error',
        description: 'Season title is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedSeriesId) {
      toast({
        title: 'Error',
        description: 'Please select a series first',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!user?.uid) {
      toast({
        title: 'Error',
        description: 'Please sign in to create a season',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Seasons are identified by seasonNumber, not as separate documents
      // Extract season number from title (e.g., "Season 1" -> "1" or just use the title)
      const seasonNumber = newSeasonTitle.trim().replace(/^season\s*/i, '').trim() || newSeasonTitle.trim();
      
      // Call the parent callback
      onNewSeason({
        title: newSeasonTitle.trim(),
        description: newSeasonDescription.trim()
      });

      // Add the season to local state immediately so it appears in dropdown
      setSeries(prevSeries => prevSeries.map(s => {
        if (s.id === selectedSeriesId) {
          // Check if season already exists
          const seasonExists = s.seasons.some(season => season.id === seasonNumber);
          if (!seasonExists) {
            return {
              ...s,
              seasons: [
                ...s.seasons,
                {
                  id: seasonNumber,
                  title: newSeasonTitle.trim(),
                  description: newSeasonDescription.trim() || `Season ${seasonNumber}`,
                  episodeCount: 0
                }
              ]
            };
          }
        }
        return s;
      }));

      // Reload series to get updated seasons (seasons are loaded from episodes)
      await loadSeries();
      
      // Select the newly created season
      onSeasonChange(seasonNumber);

      toast({
        title: 'Season Created',
        description: `"${newSeasonTitle.trim()}" has been created successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setNewSeasonTitle('');
      setNewSeasonDescription('');
      setShowNewSeason(false);
    } catch (error: any) {
      console.error('Error creating season:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create season. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <Text color={labelColor}>Loading series...</Text>
      </Box>
    );
  }

  return (
    <VStack gap={4} align="stretch">
      {/* Series Selection */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Series
        </FormLabel>
        <HStack gap={2}>
          <Select
            value={selectedSeriesId || ''}
            onChange={(e) => onSeriesChange(e.target.value || null)}
            placeholder="Select a series (optional)"
            bg={bgColor}
            borderColor={borderColor}
            disabled={disabled}
          >
            {series.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </Select>
          <IconButton
            aria-label="Add new series"
            icon={<IconWrapper icon={FiPlus} size={16} />}
            size="sm"
            variant="outline"
            onClick={() => setShowNewSeries(true)}
            disabled={disabled}
          />
        </HStack>
        {selectedSeries && (
          <Text fontSize="sm" color={labelColor} mt={1}>
            {selectedSeries.description}
          </Text>
        )}
      </FormControl>

      {/* New Series Form */}
      {showNewSeries && (
        <Box p={4} border="1px solid" borderColor={borderColor} borderRadius="md" bg={bgColor}>
          <VStack gap={3} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontWeight="bold" color={textColor}>
                Create New Series
              </Text>
              <IconButton
                aria-label="Close"
                icon={<IconWrapper icon={FiX} size={16} />}
                size="sm"
                variant="ghost"
                onClick={() => setShowNewSeries(false)}
              />
            </HStack>
            
            <Input
              value={newSeriesTitle}
              onChange={(e) => setNewSeriesTitle(e.target.value)}
              placeholder="Series title"
              bg={bgColor}
              borderColor={borderColor}
            />
            
            <Input
              value={newSeriesDescription}
              onChange={(e) => setNewSeriesDescription(e.target.value)}
              placeholder="Series description (optional)"
              bg={bgColor}
              borderColor={borderColor}
            />
            
            <HStack gap={2}>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={handleCreateSeries}
                flex={1}
              >
                Create Series
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNewSeries(false)}
                flex={1}
              >
                Cancel
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {/* Season Selection */}
      {selectedSeriesId && (
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Season
          </FormLabel>
          <HStack gap={2}>
            <Select
              value={selectedSeasonId || ''}
              onChange={(e) => onSeasonChange(e.target.value || null)}
              placeholder="Select a season (optional)"
              bg={bgColor}
              borderColor={borderColor}
              disabled={disabled}
            >
              {availableSeasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.title} ({season.episodeCount} episodes)
                </option>
              ))}
            </Select>
            <IconButton
              aria-label="Add new season"
              icon={<IconWrapper icon={FiPlus} size={16} />}
              size="sm"
              variant="outline"
              onClick={() => setShowNewSeason(true)}
              disabled={disabled}
            />
          </HStack>
          {selectedSeasonId && (
            <Text fontSize="sm" color={labelColor} mt={1}>
              {availableSeasons.find(s => s.id === selectedSeasonId)?.description}
            </Text>
          )}
        </FormControl>
      )}

      {/* New Season Form */}
      {showNewSeason && selectedSeriesId && (
        <Box p={4} border="1px solid" borderColor={borderColor} borderRadius="md" bg={bgColor}>
          <VStack gap={3} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontWeight="bold" color={textColor}>
                Create New Season
              </Text>
              <IconButton
                aria-label="Close"
                icon={<IconWrapper icon={FiX} size={16} />}
                size="sm"
                variant="ghost"
                onClick={() => setShowNewSeason(false)}
              />
            </HStack>
            
            <Input
              value={newSeasonTitle}
              onChange={(e) => setNewSeasonTitle(e.target.value)}
              placeholder="Season title"
              bg={bgColor}
              borderColor={borderColor}
            />
            
            <Input
              value={newSeasonDescription}
              onChange={(e) => setNewSeasonDescription(e.target.value)}
              placeholder="Season description (optional)"
              bg={bgColor}
              borderColor={borderColor}
            />
            
            <HStack gap={2}>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={handleCreateSeason}
                flex={1}
              >
                Create Season
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNewSeason(false)}
                flex={1}
              >
                Cancel
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {/* Summary */}
      {selectedSeriesId && selectedSeasonId && (
        <Box p={3} bg="purple.50" _dark={{ bg: 'purple.900' }} borderRadius="md">
          <VStack gap={1} align="start">
            <Text fontSize="sm" fontWeight="bold" color="purple.700" _dark={{ color: 'purple.200' }}>
              Video will be added to:
            </Text>
            <Text fontSize="sm" color="purple.600" _dark={{ color: 'purple.300' }}>
              {selectedSeries?.title} → {availableSeasons.find(s => s.id === selectedSeasonId)?.title}
            </Text>
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

export default SeriesSeasonPicker;
